import { Injectable } from '@angular/core';
import { NotifierService } from '@shared/core';
import { OperatorStatus, OperatorStatusData } from '@shared/core/types/operator';

import { BehaviorSubject, fromEvent, Subscription, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { OperatorApiService } from './operator-api.service';
import { Translation, TranslocoService } from '@ngneat/transloco';
import { registerTranslationsManually } from '@skyeng/libs/shared/i18n';
import { filterEmpty } from '@skyeng/libs/shared/rxjs';

const POOLING_INTERVAL = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class OperatorStatusService {
  enabled$ = new BehaviorSubject(false);
  readonly operatorStatus$: BehaviorSubject<OperatorStatus> = new BehaviorSubject(OperatorStatus.Offline);
  readonly operatorStatusTime$: BehaviorSubject<number | null> = new BehaviorSubject(null);

  broadcastChannel = new BroadcastChannel('crm-operator-status');

  // Для возможности гибко управлять процессом переходов, задаем коллбеки обработки для конкретных статусов
  statusCallbacks: { [statusName: string]: () => void } = {
    [OperatorStatus.WorkingOnTasks]: () => this.setOperatorStatus(OperatorStatus.WorkingOnTasks),
    [OperatorStatus.Break]: () => this.setOperatorStatus(OperatorStatus.Break),
    [OperatorStatus.Training]: () => this.setOperatorStatus(OperatorStatus.Training),
    [OperatorStatus.Meeting]: () => this.setOperatorStatus(OperatorStatus.Meeting),
    [OperatorStatus.AdministrativeWork]: () => this.setOperatorStatus(OperatorStatus.AdministrativeWork),
    [OperatorStatus.WorkingInAnotherDep]: () => this.setOperatorStatus(OperatorStatus.WorkingInAnotherDep),
    [OperatorStatus.Offline]: () => this.setOperatorStatus(OperatorStatus.Offline),
  };

  constructor(
    private translocoService: TranslocoService,
    private operatorApiService: OperatorApiService,
    private notifier: NotifierService,
  ) {
    registerTranslationsManually(
      'personTasksList',
      this.translocoService,
      import.meta.webpackContext!('../../../crm/persons/tasks/page/translation'),
    );

    this.operatorApiService
      .getCurrentOperator()
      .pipe(take(1), filterEmpty())
      .subscribe(() => {
        this.initOperatorStatus();
      });

    fromEvent<MessageEvent>(this.broadcastChannel, 'message')
      .pipe(map(({ data }) => data as OperatorStatusData))
      .subscribe(data => {
        this.operatorStatus$.next(data.type);
        this.operatorStatusTime$.next(new Date(data.startsAt || new Date()).getTime());
      });
  }

  getCurrentStatus(): OperatorStatus {
    return this.operatorStatus$.getValue();
  }

  getCurrentStatusTime(): number {
    return this.operatorStatusTime$.getValue();
  }

  getAvailableStatuses(): OperatorStatus[] {
    return Object.values(OperatorStatus);
  }

  changeStatus(newStatus: OperatorStatus): void {
    if (this.getCurrentStatus() === newStatus || this.statusCallbacks[newStatus] === undefined) {
      return;
    }

    this.statusCallbacks[newStatus]();
  }

  private setOperatorStatus(status: OperatorStatus): Subscription {
    return this.operatorApiService.updateStatus(status).subscribe(() => {
      this.operatorStatus$.next(status);
      this.operatorStatusTime$.next(Date.now());
      this.broadcastChannel.postMessage({
        type: status,
      });
    });
  }

  updateExpireOperatorStatus(): void {
    const lang = this.translocoService.getActiveLang();
    const translation: Translation = this.translocoService.getTranslation(lang);
    this.operatorApiService.getStatusInfo().subscribe(info => {
      if (this.getCurrentStatus() !== info.type) {
        this.operatorStatus$.next(info.type);
        this.operatorStatusTime$.next(new Date(info.startsAt).getTime());
        this.notifier.show(translation['personTasksList.statusWasUpdated']);
      } else {
        this.notifier.show(translation['personTasksList.statusNotWorkingOnTasks']);
      }
    });
  }

  private initOperatorStatus(): void {
    timer(0, POOLING_INTERVAL)
      .pipe(
        switchMap(() => this.operatorApiService.getStatusInfo()),
        filter(info => !!info && !!info.type && !!info.startsAt),
      )
      .subscribe(info => {
        this.enabled$.next(true);
        this.operatorStatus$.next(info.type);
        this.operatorStatusTime$.next(new Date(info.startsAt).getTime());
        this.broadcastChannel.postMessage(info);
      });

    this.operatorApiService
      .getStatusInfo()
      .pipe(filter(info => !!info && !!info.type && !!info.startsAt))
      .subscribe(info => {
        this.enabled$.next(true);
        this.operatorStatus$.next(info.type);
        this.operatorStatusTime$.next(new Date(info.startsAt).getTime());
      });
  }
}
